import { useEffect, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import type { QuestionListItem } from '@course-authoring/shared-types';
import { ListPagination, type AllowedPageSize } from '../table/ListPagination';
import { QUESTION_BANK_BROWSE_COPY as COPY } from './questionBankBrowseCopy';
import { formatAddedDate } from './QuestionBankDetail';
import type { QuestionBankBrowseStyles } from './questionBankBrowseStyles';

/** The card shows at most this many of the question's concepts (Sara's writeup, 2026-11-03); the detail lists them all. */
const CARD_CONCEPTS = 3;

export interface QuestionBankCardsProps {
  styles: QuestionBankBrowseStyles;
  status: 'pending' | 'error' | 'success';
  items: readonly QuestionListItem[];
  /** "Recently added" and "Most used in lessons" add their value to each card. */
  sortKey: string;
  /** The previous page while the next one loads: shown, not activatable. */
  stale: boolean;
  total: number;
  page: number;
  pageSize: AllowedPageSize;
  onOpen: (item: QuestionListItem) => void;
  onRetry: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: AllowedPageSize) => void;
  /** Rendered at the right of the pagination row, and alone under the empty state or the load error (the page's "Manage questions"). */
  foot?: ReactNode;
  /**
   * The picker (Sara's "Selecting a question from the in-flow picker", 2026-11-04): each card ends in "Details" and a
   * "Select" pill, shown on hover and focus-within, and Select picks the question without opening the detail. Without
   * it the card ends in an ellipsis on hover and focus (the page).
   */
  onSelect?: (item: QuestionListItem) => void;
  /** A pick is in flight: Select is disabled. */
  busy?: boolean;
  /** Server rejections keyed by question id, shown under that question's card. */
  errors?: Readonly<Record<string, string>>;
}

/**
 * The bank's results: loading, error with Retry, the empty state, or one
 * card per question (text, then one line: its concepts, comma-separated, up
 * to three; "N responses" with N compact; the sort's value; "Q<id>" last;
 * middots between the fields) followed by the shared list pagination. The
 * concepts are not links here: the whole card opens the detail, where they
 * are.
 */
export function QuestionBankCards({
  styles,
  status,
  items,
  sortKey,
  stale,
  total,
  page,
  pageSize,
  onOpen,
  onRetry,
  onPageChange,
  onPageSizeChange,
  foot,
  onSelect,
  busy = false,
  errors = {},
}: QuestionBankCardsProps) {
  const b = styles.browse;
  const c = styles.centerstage;

  const sortNote = (item: QuestionListItem): string | null => {
    if (sortKey === 'createdAt') return COPY.added(formatAddedDate(item.createdAt));
    if (sortKey === 'usedIn') return COPY.usedIn(item.usageCount);
    return null;
  };

  const footRow = foot ? <div style={b.listFoot}>{foot}</div> : null;
  if (status === 'pending') return <div role="status" style={c.loadingPanel}>{COPY.loading}</div>;
  if (status === 'error') {
    return (
      <>
        <div role="alert" style={c.errorPanel}>
          <p style={c.emptyBody}>{COPY.loadError}</p>
          <button type="button" style={c.secondaryButton} onClick={onRetry}>{COPY.retry}</button>
        </div>
        {footRow}
      </>
    );
  }
  if (items.length === 0) {
    return (
      <>
        <div style={c.emptyPanel}>
          <p style={c.emptyTitle}>{COPY.emptyTitle}</p>
          <p style={c.emptyBody}>{COPY.emptyBody}</p>
        </div>
        {footRow}
      </>
    );
  }
  const pagination = (
    <ListPagination
      page={page}
      pageSize={pageSize}
      totalCount={total}
      isPlaceholderData={stale}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    />
  );
  return (
    <>
      <ul style={b.list} data-testid="question-bank-list">
        {items.map((item) => (
          <li key={item.questionDefinitionId}>
            <QuestionCard
              styles={styles}
              item={item}
              note={sortNote(item)}
              stale={stale}
              busy={busy}
              error={errors[String(item.questionDefinitionId)]}
              onOpen={onOpen}
              onSelect={onSelect}
            />
          </li>
        ))}
      </ul>
      {foot ? (
        <div style={b.listFoot}>
          <div style={b.listFootPagination}>{pagination}</div>
          {foot}
        </div>
      ) : pagination}
    </>
  );
}

interface QuestionCardProps {
  styles: QuestionBankBrowseStyles;
  item: QuestionListItem;
  note: string | null;
  stale: boolean;
  busy: boolean;
  error?: string;
  onOpen: (item: QuestionListItem) => void;
  onSelect?: (item: QuestionListItem) => void;
}

/**
 * One card: the whole card is a button that opens the detail. With `onSelect`
 * (the picker) its right edge holds "Details", a label for what the card
 * already does, and a "Select" pill that picks the question, a sibling of
 * the card button, not nested; both fade in on hover and on focus-within
 * with their space held at rest. Without it (the page) the right edge holds
 * an ellipsis on hover and focus.
 */
function QuestionCard({ styles, item, note, stale, busy, error, onOpen, onSelect }: QuestionCardProps) {
  const b = styles.browse;
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [detailsHovered, setDetailsHovered] = useState(false);
  const [selectHovered, setSelectHovered] = useState(false);
  const cardRef = useRef<HTMLButtonElement | null>(null);
  const selectRef = useRef<HTMLButtonElement | null>(null);
  const picked = useRef(false);
  const wasBusy = useRef(false);
  const active = hovered || focused;
  // Select is disabled while the pick is in flight, which drops focus; when the pick settles with the picker still
  // open (a rejection), take focus back so Escape keeps working.
  useEffect(() => {
    if (wasBusy.current && !busy && picked.current) {
      picked.current = false;
      if (!document.activeElement?.closest('[role="dialog"]')) selectRef.current?.focus();
    }
    wasBusy.current = busy;
  }, [busy]);
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
  };
  const line = [
    ...(item.concepts.length > 0 ? [item.concepts.slice(0, CARD_CONCEPTS).map((concept) => concept.name).join(', ')] : []),
    COPY.responsesCompact(item.responseCount),
    ...(note ? [note] : []),
    `Q${item.questionDefinitionId}`,
  ].join(' · ');

  return (
    <div
      style={b.cardWrap}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={onBlur}
    >
      <button
        ref={cardRef}
        type="button"
        style={{ ...b.card, ...(onSelect ? b.cardWithActions : {}), ...(hovered ? b.cardHover : {}) }}
        onClick={() => onOpen(item)}
        disabled={stale}
      >
        <span style={b.cardText}>
          <span style={b.cardQuestion}>{item.questionTextPlain}</span>
          <span style={b.cardMeta}>{line}</span>
        </span>
        {onSelect ? null : (
          <span aria-hidden="true" data-testid="card-glyph" style={{ ...b.cardGlyph, ...(active ? {} : { opacity: 0 }) }}>
            <FontAwesomeIcon icon={faEllipsis} />
          </span>
        )}
      </button>
      {onSelect ? (
        <span data-testid="card-actions" style={{ ...b.cardActions, opacity: active ? 1 : 0, pointerEvents: active ? 'auto' : 'none' }}>
          {/* Names what the card already does, so it opens the detail like the card under it; the card takes focus first,
              so the detail hands focus back to it (a span cannot hold focus, and the dialog would restore it to the picker's frame). */}
          <span
            aria-hidden="true"
            style={{ ...b.cardDetails, ...(detailsHovered ? b.cardDetailsHover : {}) }}
            onMouseEnter={() => setDetailsHovered(true)}
            onMouseLeave={() => setDetailsHovered(false)}
            onClick={() => { if (stale) return; cardRef.current?.focus(); onOpen(item); }}
          >
            {COPY.details}
          </span>
          <button
            ref={selectRef}
            type="button"
            aria-label={COPY.selectQuestion(item.questionTextPlain)}
            disabled={stale || busy}
            onClick={() => { picked.current = true; onSelect(item); }}
            onMouseEnter={() => setSelectHovered(true)}
            onMouseLeave={() => setSelectHovered(false)}
            style={{ ...b.cardSelect, ...(selectHovered && !busy ? b.cardSelectHover : {}), ...(busy ? { cursor: 'default' } : {}) }}
          >
            {COPY.select}
          </button>
        </span>
      ) : null}
      {error ? <p role="alert" style={b.cardError}>{error}</p> : null}
    </div>
  );
}
